export type ContactSubmissionType =
  | "bug_report"
  | "feedback"
  | "feature_request"
  | "removal_request"
  | "general_inquiry";

export type RemovalRelationship =
  | "business_owner"
  | "authorized_employee"
  | "authorized_representative"
  | "person_named_in_listing"
  | "legal_representative"
  | "other";

export interface FieldConfig {
  visible: boolean;
  required: boolean;
  label: string;
  helperText: string;
  placeholder?: string;
}

export interface ContactTypeConfig {
  canonical: ContactSubmissionType;
  label: string;
  description: string;
  notice?: string;
  fields: {
    name: FieldConfig;
    email: FieldConfig;
    businessUrl: FieldConfig;
    message: FieldConfig;
    relationship?: FieldConfig;
    supportingInfo?: FieldConfig;
    bugBrowser?: FieldConfig;
    bugDevice?: FieldConfig;
    bugSteps?: FieldConfig;
  };
}

export const CANONICAL_TYPE_MAP: Record<string, ContactSubmissionType> = {
  bug_report: "bug_report",
  bug: "bug_report",
  "bug report": "bug_report",
  feedback: "feedback",
  feature_request: "feature_request",
  "feature request": "feature_request",
  removal_request: "removal_request",
  "removal request": "removal_request",
  "request removal": "removal_request",
  general_inquiry: "general_inquiry",
  "general inquiry": "general_inquiry"
};

export const CONTACT_TYPE_CONFIG: Record<ContactSubmissionType, ContactTypeConfig> = {
  bug_report: {
    canonical: "bug_report",
    label: "Bug Report",
    description: "Report technical errors, broken links, or visual glitches.",
    fields: {
      name: {
        visible: true,
        required: false,
        label: "Name (optional)",
        helperText: "If you'd like us to know who found the issue.",
        placeholder: "e.g. Alex Smith"
      },
      email: {
        visible: true,
        required: false,
        label: "Email for follow-up (optional)",
        helperText: "Provide an email if you'd like a response or to be notified when the issue is fixed.",
        placeholder: "alex@example.com"
      },
      businessUrl: {
        visible: true,
        required: false,
        label: "Shared business link (optional)",
        helperText: "Paste the BizNearYou business link you copied from the business details page.",
        placeholder: "https://getbiznearyou.com/?sharedBusinessId=ChIJ..."
      },
      message: {
        visible: true,
        required: true,
        label: "Describe the issue",
        helperText: "Tell us exactly what happened and how we can reproduce it.",
        placeholder: "Explain the bug or unexpected behavior..."
      },
      bugBrowser: {
        visible: true,
        required: false,
        label: "Browser or OS (optional)",
        helperText: "e.g. Chrome 120 on macOS, Safari on iOS 17",
        placeholder: "e.g. Chrome on Windows 11"
      },
      bugDevice: {
        visible: true,
        required: false,
        label: "Device model (optional)",
        helperText: "e.g. iPhone 15 Pro, Dell XPS Desktop",
        placeholder: "e.g. Macbook Pro 16"
      },
      bugSteps: {
        visible: true,
        required: false,
        label: "Steps to reproduce (optional)",
        helperText: "List the step-by-step actions to reproduce the issue.",
        placeholder: "1. Open search page...\n2. Click filter button..."
      }
    }
  },
  feedback: {
    canonical: "feedback",
    label: "Feedback",
    description: "Share your suggestions or user experience ideas with our team.",
    fields: {
      name: {
        visible: true,
        required: false,
        label: "Name (optional)",
        helperText: "So we know who shared the feedback.",
        placeholder: "e.g. Jordan Lee"
      },
      email: {
        visible: true,
        required: false,
        label: "Email for a response (optional)",
        helperText: "Only needed if you'd like a reply.",
        placeholder: "jordan@example.com"
      },
      businessUrl: {
        visible: false,
        required: false,
        label: "Business URL (hidden)",
        helperText: ""
      },
      message: {
        visible: true,
        required: true,
        label: "Your Feedback",
        helperText: "We'd love to hear your ideas and suggestions.",
        placeholder: "Share your thoughts or recommendations..."
      }
    }
  },
  feature_request: {
    canonical: "feature_request",
    label: "Feature Request",
    description: "Propose new features or capabilities for BizNearYou.",
    fields: {
      name: {
        visible: true,
        required: false,
        label: "Name (optional)",
        helperText: "So we know who requested this.",
        placeholder: "e.g. Sam Taylor"
      },
      email: {
        visible: true,
        required: false,
        label: "Email for updates or questions (optional)",
        helperText: "Provide an email if you'd like to be notified about feature updates.",
        placeholder: "sam@example.com"
      },
      businessUrl: {
        visible: false,
        required: false,
        label: "Business URL (hidden)",
        helperText: ""
      },
      message: {
        visible: true,
        required: true,
        label: "Feature Description",
        helperText: "Tell us what feature you'd like to see and how it would help.",
        placeholder: "Describe the functionality and why it would be valuable..."
      }
    }
  },
  removal_request: {
    canonical: "removal_request",
    label: "Request Removal",
    description: "Submit a request to remove or correct a public business profile.",
    notice: "Submitting a removal request does NOT automatically remove a listing. BizNearYou may verify your identity and relationship before taking action. False or fraudulent requests may be rejected.",
    fields: {
      name: {
        visible: true,
        required: true,
        label: "Full Legal or Professional Name",
        helperText: "Required so we can verify who is requesting the removal.",
        placeholder: "e.g. Jane Doe"
      },
      email: {
        visible: true,
        required: true,
        label: "Contact Email",
        helperText: "Required so we can contact you during the verification process.",
        placeholder: "jane@company.com"
      },
      businessUrl: {
        visible: true,
        required: true,
        label: "Shared business link",
        helperText: "Paste the BizNearYou business link you copied from the business details page.",
        placeholder: "https://getbiznearyou.com/?sharedBusinessId=ChIJ..."
      },
      relationship: {
        visible: true,
        required: true,
        label: "Relationship to Listing",
        helperText: "Select your relationship to the business or individual named."
      },
      message: {
        visible: true,
        required: true,
        label: "Reason for removal request",
        helperText: "Explain why this listing should be removed or modified.",
        placeholder: "Provide detailed justification for removal..."
      },
      supportingInfo: {
        visible: true,
        required: false,
        label: "Supporting Information (optional)",
        helperText: "You may include supporting information that helps verify your request.",
        placeholder: "Additional context, authorization details, or reference numbers..."
      }
    }
  },
  general_inquiry: {
    canonical: "general_inquiry",
    label: "General Inquiry",
    description: "Ask general questions about BizNearYou services.",
    fields: {
      name: {
        visible: true,
        required: false,
        label: "Name (optional)",
        helperText: "So we know who we're speaking with.",
        placeholder: "e.g. Morgan Reed"
      },
      email: {
        visible: true,
        required: true,
        label: "Email for reply",
        helperText: "We need an email so we can reply.",
        placeholder: "morgan@example.com"
      },
      businessUrl: {
        visible: true,
        required: false,
        label: "Shared business link (optional)",
        helperText: "Paste the BizNearYou business link you copied from the business details page.",
        placeholder: "https://getbiznearyou.com/?sharedBusinessId=ChIJ..."
      },
      message: {
        visible: true,
        required: true,
        label: "Message",
        helperText: "How can we help you?",
        placeholder: "Write your question or request here..."
      }
    }
  }
};

export const RELATIONSHIP_OPTIONS: { value: RemovalRelationship; label: string }[] = [
  { value: "business_owner", label: "Business Owner" },
  { value: "authorized_employee", label: "Authorized Employee" },
  { value: "authorized_representative", label: "Authorized Representative" },
  { value: "person_named_in_listing", label: "Person Named In Listing" },
  { value: "legal_representative", label: "Legal Representative" },
  { value: "other", label: "Other" }
];

export interface ContactValidationResult {
  error?: string;
  code?: string;
  fieldErrors?: Record<string, string>;
  isSpam?: boolean;
  data?: {
    submissionType: ContactSubmissionType;
    name: string | null;
    email: string | null;
    businessUrl: string | null;
    relationshipToBusiness: RemovalRelationship | null;
    message: string;
    supportingInfo: string | null;
    bugBrowser: string | null;
    bugDevice: string | null;
    bugSteps: string | null;
    isAnonymous: boolean;
  };
}

const hasControlChars = (str: string) => /[\x00-\x1F\x7F-\x9F]/.test(str);
const hasNullByte = (str: string) => str.includes("\x00");

export function validateContactSubmission(body: any): ContactValidationResult {
  if (!body || typeof body !== "object") {
    return { error: "Invalid payload: Request body must be a JSON object.", code: "CONTACT_VALIDATION_FAILED" };
  }

  // 1. Bot Honeypot Check
  const honeypot = body.honeypot || body.website || body.address_confirm;
  if (honeypot !== undefined && honeypot !== null && String(honeypot).trim().length > 0) {
    return { isSpam: true };
  }

  // 2. Normalize and validate Submission Type
  const rawType = String(body.submissionType || body.submission_type || body.subject || "").trim().toLowerCase();
  const canonicalType = CANONICAL_TYPE_MAP[rawType];

  if (!canonicalType || !CONTACT_TYPE_CONFIG[canonicalType]) {
    return {
      error: "Validation failure: Please select a valid request type.",
      code: "CONTACT_VALIDATION_FAILED"
    };
  }

  const typeConfig = CONTACT_TYPE_CONFIG[canonicalType];
  const fields = typeConfig.fields;

  // 3. Name Validation
  let cleanName: string | null = null;
  const rawName = body.name;
  if (fields.name.visible) {
    if (fields.name.required) {
      if (!rawName || typeof rawName !== "string" || rawName.trim().length === 0) {
        const msg = `${fields.name.label} is required.`;
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { name: msg } };
      }
    }
    if (rawName && typeof rawName === "string" && rawName.trim().length > 0) {
      const trimmed = rawName.trim();
      if (trimmed.length < 2 || trimmed.length > 100) {
        const msg = "Name must be between 2 and 100 characters.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { name: msg } };
      }
      if (hasControlChars(trimmed)) {
        const msg = "Name contains invalid control characters.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { name: msg } };
      }
      cleanName = trimmed;
    }
  }

  // 4. Email Validation
  let cleanEmail: string | null = null;
  const rawEmail = body.email;
  if (fields.email.visible) {
    if (fields.email.required) {
      if (!rawEmail || typeof rawEmail !== "string" || rawEmail.trim().length === 0) {
        const msg = `${fields.email.label} is required.`;
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { email: msg } };
      }
    }
    if (rawEmail && typeof rawEmail === "string" && rawEmail.trim().length > 0) {
      const trimmedEmail = rawEmail.trim().toLowerCase();
      if (trimmedEmail.length > 254) {
        const msg = "Email address must not exceed 254 characters.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { email: msg } };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        const msg = "Please provide a valid email address format.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { email: msg } };
      }
      // Check header injection
      if (/[\r\n]/.test(trimmedEmail)) {
        const msg = "Email address contains invalid characters.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { email: msg } };
      }
      cleanEmail = trimmedEmail;
    }
  }

  // 5. Business URL Validation
  let cleanBusinessUrl: string | null = null;
  const rawUrl = body.businessUrl || body.business_url;
  if (fields.businessUrl.visible) {
    if (fields.businessUrl.required) {
      if (!rawUrl || typeof rawUrl !== "string" || rawUrl.trim().length === 0) {
        const msg = `${fields.businessUrl.label} is required.`;
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { businessUrl: msg } };
      }
    }
    if (rawUrl && typeof rawUrl === "string" && rawUrl.trim().length > 0) {
      const trimmedUrl = rawUrl.trim();
      if (trimmedUrl.length > 2048) {
        const msg = "URL must not exceed 2048 characters.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { businessUrl: msg } };
      }
      try {
        const parsedUrl = new URL(trimmedUrl);
        if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
          const msg = "URL must use a secure web protocol (http or https).";
          return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { businessUrl: msg } };
        }
        cleanBusinessUrl = trimmedUrl;
      } catch (e) {
        const msg = "Please provide a valid URL format.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { businessUrl: msg } };
      }
    }
  }

  // 6. Relationship Validation (Removal Requests)
  let cleanRelationship: RemovalRelationship | null = null;
  const rawRelationship = body.relationship || body.relationshipToBusiness || body.relationship_to_business;
  if (fields.relationship?.visible) {
    if (fields.relationship.required) {
      if (!rawRelationship || typeof rawRelationship !== "string" || rawRelationship.trim().length === 0) {
        const msg = "Please select your relationship to the business or listing.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { relationshipToBusiness: msg } };
      }
    }
    if (rawRelationship && typeof rawRelationship === "string" && rawRelationship.trim().length > 0) {
      const trimmedRel = rawRelationship.trim().toLowerCase() as RemovalRelationship;
      const validRelValues = RELATIONSHIP_OPTIONS.map(o => o.value);
      if (!validRelValues.includes(trimmedRel)) {
        const msg = "Invalid relationship selection.";
        return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { relationshipToBusiness: msg } };
      }
      cleanRelationship = trimmedRel;
    }
  }

  // 7. Message Validation
  let cleanMessage = "";
  const rawMessage = body.message;
  if (!rawMessage || typeof rawMessage !== "string" || rawMessage.trim().length === 0) {
    const msg = `${fields.message.label} is required.`;
    return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { message: msg } };
  }
  const trimmedMsg = rawMessage.trim();
  if (trimmedMsg.length < 10) {
    const msg = "Message must be at least 10 characters long.";
    return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { message: msg } };
  }
  if (trimmedMsg.length > 5000) {
    const msg = "Message must not exceed 5000 characters.";
    return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { message: msg } };
  }
  if (hasNullByte(trimmedMsg) || hasControlChars(trimmedMsg)) {
    const msg = "Message contains invalid control characters or null bytes.";
    return { error: `Validation failure: ${msg}`, code: "CONTACT_VALIDATION_FAILED", fieldErrors: { message: msg } };
  }
  cleanMessage = trimmedMsg;

  // 8. Optional Fields
  let cleanSupportingInfo: string | null = null;
  if (fields.supportingInfo?.visible && body.supportingInfo && typeof body.supportingInfo === "string") {
    const trimmed = body.supportingInfo.trim();
    if (trimmed.length > 0) cleanSupportingInfo = trimmed.substring(0, 2000);
  }

  let cleanBugBrowser: string | null = null;
  let cleanBugDevice: string | null = null;
  let cleanBugSteps: string | null = null;

  if (canonicalType === "bug_report") {
    if (body.bugBrowser && typeof body.bugBrowser === "string" && body.bugBrowser.trim().length > 0) {
      cleanBugBrowser = body.bugBrowser.trim().substring(0, 500);
    }
    if (body.bugDevice && typeof body.bugDevice === "string" && body.bugDevice.trim().length > 0) {
      cleanBugDevice = body.bugDevice.trim().substring(0, 500);
    }
    if (body.bugSteps && typeof body.bugSteps === "string" && body.bugSteps.trim().length > 0) {
      cleanBugSteps = body.bugSteps.trim().substring(0, 1000);
    }
  }

  return {
    data: {
      submissionType: canonicalType,
      name: cleanName,
      email: cleanEmail,
      businessUrl: cleanBusinessUrl,
      relationshipToBusiness: cleanRelationship,
      message: cleanMessage,
      supportingInfo: cleanSupportingInfo,
      bugBrowser: cleanBugBrowser,
      bugDevice: cleanBugDevice,
      bugSteps: cleanBugSteps,
      isAnonymous: cleanEmail === null
    }
  };
}

export interface OriginValidationResult {
  allowed: boolean;
  originHostname: string;
  reason?: string;
}

export function isAllowedContactOrigin(
  originHeader?: string,
  refererHeader?: string,
  xForwardedHostHeader?: string,
  hostHeader?: string
): OriginValidationResult {
  const primaryOrigin = originHeader || refererHeader;
  const xForwardedHost = (xForwardedHostHeader || "").split(",")[0].trim().toLowerCase();
  const rawHost = (hostHeader || "").split(":")[0].trim().toLowerCase();
  const requestHost = xForwardedHost || rawHost;

  let originHostname = "";
  if (primaryOrigin) {
    try {
      const originUrl = new URL(primaryOrigin);
      originHostname = originUrl.hostname.toLowerCase();
    } catch (e) {
      return { allowed: false, originHostname: "", reason: "Malformed Origin or Referer header." };
    }
  } else if (requestHost) {
    originHostname = requestHost;
  }

  if (!originHostname) {
    return { allowed: false, originHostname: "", reason: "Missing Origin, Referer, or Host header." };
  }

  const productionHostnames = new Set([
    "getbiznearyou.com",
    "www.getbiznearyou.com"
  ]);

  const devHostnames = new Set([
    "localhost",
    "127.0.0.1",
    "0.0.0.0"
  ]);

  const envHostnames = new Set<string>();
  const envVars = [
    process.env.APP_ORIGIN,
    process.env.APP_URL,
    process.env.PREVIEW_ORIGIN,
    process.env.APP_PREVIEW_HOST,
    ...(process.env.ALLOWED_ORIGINS ? process.env.ALLOWED_ORIGINS.split(",") : [])
  ];

  for (const envVal of envVars) {
    if (envVal) {
      const trimmed = envVal.trim();
      if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        try {
          envHostnames.add(new URL(trimmed).hostname.toLowerCase());
        } catch (_) {}
      } else if (trimmed) {
        envHostnames.add(trimmed.toLowerCase());
      }
    }
  }

  if (productionHostnames.has(originHostname) || envHostnames.has(originHostname) || devHostnames.has(originHostname)) {
    return { allowed: true, originHostname };
  }

  if (requestHost && originHostname === requestHost) {
    return { allowed: true, originHostname };
  }

  const isPreviewPattern =
    originHostname.endsWith(".run.app") ||
    originHostname.endsWith(".us-west2.run.app") ||
    originHostname.endsWith(".googleusercontent.com");

  if (isPreviewPattern) {
    if (process.env.NODE_ENV !== "production" || requestHost.endsWith(".run.app") || originHostname === requestHost) {
      return { allowed: true, originHostname };
    }
  }

  return {
    allowed: false,
    originHostname,
    reason: `Hostname '${originHostname}' does not match expected production, development, or preview origins.`
  };
}
